import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleYesterdaysPerformance,
  type HuddleYesterdaysPerformanceResult,
  getHuddleYesterdaysPerformanceOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleYesterdaysPerformanceRowsToTable,
  mapHuddleYesterdaysPerformanceSummaryToMetrics,
  type HuddleYesterdaysPerformanceTableRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleYesterdaysPerformanceOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleYesterdaysPerformanceResult = {
  data: HuddleYesterdaysPerformanceResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  providerRows: HuddleYesterdaysPerformanceTableRow[]
}

function useHuddleYesterdaysPerformanceFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleYesterdaysPerformanceOptions & {
  fetcher: HuddleDateFetcher<HuddleYesterdaysPerformanceResult>
}): UseHuddleYesterdaysPerformanceResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: "Failed to load yesterday's performance",
  })

  const summaryMetrics = useMemo(
    () => mapHuddleYesterdaysPerformanceSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const providerRows = useMemo(
    () => mapHuddleYesterdaysPerformanceRowsToTable(data?.providers),
    [data?.providers],
  )

  return { data, loading, error, summaryMetrics, providerRows }
}

export function useHuddleYesterdaysPerformance(
  options: UseHuddleYesterdaysPerformanceOptions,
): UseHuddleYesterdaysPerformanceResult {
  return useHuddleYesterdaysPerformanceFromFetcher({
    ...options,
    fetcher: getHuddleYesterdaysPerformance,
  })
}
export function useHuddleYesterdaysPerformanceOverview(
  options: UseHuddleYesterdaysPerformanceOptions,
): UseHuddleYesterdaysPerformanceResult {
  return useHuddleYesterdaysPerformanceFromFetcher({
    ...options,
    fetcher: getHuddleYesterdaysPerformanceOverview,
  })
}
